import type { FaqEntry } from '../faq';

/**
 * English FAQ, translated from `es.ts`, which is the source.
 *
 * Product names follow the catalogs so the page and the rest of the site agree: `Redondeo Solidario`
 * is "Charity Rounding Up", `entidades sociales` is "social entities", `personas donantes` is
 * "donors". Second person is "you", as the Spanish uses `tú`.
 */
export const faqEntriesEn: FaqEntry[] = [
	// MARK: 1. For companies
	{
		block: 'companies',
		question: 'What is Worldcoo?',
		answer:
			'Worldcoo is a technology company that connects businesses, people and social entities to enable new ways of donating and to generate social and environmental impact. Since 2012 we have been building solutions that integrate donating into companies’ purchase channels and turn small everyday gestures into opportunities for impact.',
	},
	{
		block: 'companies',
		question: 'What solutions does Worldcoo offer?',
		answer:
			'Our aim is to make it easy to build solidarity into the different points of contact between companies and their customers or employees. Worldcoo develops several donation solutions that adapt to each company’s channels and needs. They include the Charity Rounding Up, ecommerce donations, donation portals and donation solutions tied to payroll or invoices.',
	},
	{
		block: 'companies',
		question: 'What is the Charity Rounding Up?',
		answer:
			'The Charity Rounding Up lets a company’s customers make a small donation when they complete a purchase. The customer can round up the amount of their purchase and donate the difference to a selected social or environmental project.',
	},
	{
		block: 'companies',
		question: 'What does it cost to add Worldcoo?',
		answer:
			'We look at each case individually, taking into account the company’s needs, the solution it wants to implement and the kind of technical integration required. Depending on the operational side and the implementation needed, adding Worldcoo’s solutions can be free of charge.',
	},
	{
		block: 'companies',
		question: 'Do I have to change my payment system to add Worldcoo?',
		answer:
			'Worldcoo works with a range of technology providers and payment systems to integrate its solutions into each company’s channels. Our team reviews each case to identify the best integration option.',
	},
	{
		block: 'companies',
		question: 'In which channels can I activate Worldcoo?',
		answer:
			'Worldcoo’s solutions can be integrated into different points of contact with the customer, both physical and digital, and with employees too:',
		bullets: ['Shops and physical premises.', 'Ecommerce.', 'Apps.', 'Online donation portals', 'Payroll and invoices'],
		outro: 'The solution adapts to the characteristics and needs of each company.',
	},
	{
		block: 'companies',
		question: 'Can I choose how much the customer donates?',
		answer:
			'Yes. We work with each company to identify social or environmental projects aligned with its values, objectives and priorities. Worldcoo proposes a selection of projects that may fit each company, but the final decision on which cause to support always rests with the company.',
	},
	{
		block: 'companies',
		question: 'Can I choose which cause or project I want to support?',
		answer:
			'Yes. Worldcoo works with companies to identify projects aligned with their values, impact objectives and social or environmental priorities. Worldcoo puts forward a proposal of projects it believes may fit that particular company, but the company has the last word on the project chosen.',
	},
	{
		block: 'companies',
		question: 'Can I support local projects?',
		answer:
			'Yes. At Worldcoo we work with social entities of different sizes and fields of work, including local organisations running projects with a great impact in their communities. One of our aims is to open access to funding for these entities too, and to give visibility to the important work they do.',
	},
	{
		block: 'companies',
		question: 'Can I change project?',
		answer:
			'Yes. Campaigns can be updated when a project reaches its funding goal, when it ends, or when the company decides to support a new cause.',
	},
	{
		block: 'companies',
		question: 'Can I find out how much has been raised?',
		answer:
			'Yes. Worldcoo provides tracking tools that show how campaigns are progressing and the impact generated, daily and broken down by premises.',
	},
	{
		block: 'companies',
		question: 'Can I communicate the results of the campaign?',
		answer:
			'Yes. Worldcoo provides information on how campaigns are progressing and on the projects funded, to make it easier to communicate the impact generated.',
	},

	// MARK: 2. For social entities
	{
		block: 'ngos',
		question: 'How can my entity raise funds through Worldcoo?',
		answer:
			'Social entities can put their projects forward to Worldcoo to be considered for the fundraising campaigns run through partner companies. Our team reviews each proposal and, when there is an opportunity to collaborate, works with the entity to launch the campaign.',
	},
	{
		block: 'ngos',
		question: 'What kind of projects does Worldcoo fund?',
		answer: 'We work with projects across different social and environmental fields, among them:',
		bullets: [
			'Children and young people.',
			'Social inclusion.',
			'Health.',
			'Older people.',
			'Disability.',
			'The environment.',
			'Cooperation.',
			'Emergencies.',
			'Other fields of social impact.',
		],
	},
	{
		block: 'ngos',
		question: 'What requirements does an entity have to meet to work with Worldcoo?',
		answer:
			'Entities have to meet the selection criteria set by Worldcoo and provide the documentation needed to evidence their incorporation, their activity and their legal standing. They also have to be able to issue the corresponding donation certificates.',
		outro: 'You can read Worldcoo’s selection criteria for the full list of requirements.',
		link: { label: 'Selection criteria', target: 'selection-criteria' },
	},
	{
		block: 'ngos',
		question: 'How do I start collaborating?',
		answer: 'You can start the process by filling in the sign-up form with your entity’s basic information.',
		link: { label: 'Go to the sign-up form', target: 'ngo-signup' },
	},
	{
		block: 'ngos',
		question: 'Can I put a specific project forward?',
		answer:
			'Yes. Worldcoo helps fund specific projects run by social entities, rather than funding the entities in general. You can share one or more projects that need funding with us. Our team will assess how they fit our collaboration criteria and will look for the opportunities that best suit each project.',
	},
	{
		block: 'ngos',
		question: 'Can small or local entities take part?',
		answer:
			'Yes. At Worldcoo we want to help local and smaller entities gain access to new funding opportunities too. Many of these organisations do essential work in their communities and generate a very significant direct impact.',
	},
	{
		block: 'ngos',
		question: 'How does the project selection process work?',
		answer:
			'Once you are part of the Worldcoo community, your projects become part of our database. When an opportunity to collaborate comes up, we put forward the projects that best fit each company’s needs and criteria, taking into account things like the geographic and thematic scope, its CSR strategy or its customer profile.',
		outro:
			'The final decision on which organisation will take part in each campaign always rests with the partner company.',
	},
	{
		block: 'ngos',
		question: 'Once a project is funded, how are the funds raised managed?',
		answer:
			'Once the fundraising campaign ends, Worldcoo handles the transfer of 100% of the funds to the beneficiary social entity. The process is carried out in two payments:',
		bullets: [
			'A first payment of 50% of the amount raised when the campaign ends.',
			'A second payment of the remaining 50% once the entity submits the project’s monitoring and accountability report.',
		],
	},
	{
		block: 'ngos',
		question: 'What does an entity commit to when it receives funding?',
		answer:
			'Entities that receive funds through Worldcoo commit to reporting on the delivery and impact of the project funded. To do so, they have to submit a report covering:',
		bullets: [
			'How the project developed and who the beneficiaries were.',
			'The results and the impact achieved.',
			'Images or other documentation showing how the project progressed.',
			'How the funds received were used and where they went.',
		],
	},
	{
		block: 'ngos',
		question: 'What does working with Worldcoo cost a social entity?',
		answer:
			'Worldcoo offers social entities fundraising, management and monitoring services, as well as access to new opportunities to collaborate with companies. For these services, Worldcoo charges a fee on the funds managed. The terms are communicated transparently to each entity before any collaboration begins.',
	},

	// MARK: 3. For donors
	{
		block: 'donors',
		question: 'What is the Charity Rounding Up?',
		answer:
			'The Charity Rounding Up is a simple way to make a small donation when you buy something. When you pay, you can round up the amount of your purchase and put those cents towards a social or environmental project.',
	},
	{
		block: 'donors',
		question: 'Where can I make a donation?',
		answer: 'You can find Worldcoo donation solutions in the premises and digital channels of our partner companies.',
	},
	{
		block: 'donors',
		question: 'How much do I donate?',
		answer:
			'The amount of the donation depends on the solution and on the campaign you take part in. With the Charity Rounding Up, you can round up the amount of your purchase and donate the difference. In other campaigns, you can donate a fixed amount set in advance. Before you confirm your donation, you will always be able to see the amount you are about to give.',
	},
	{
		block: 'donors',
		question: 'Can I decide whether I want to donate?',
		answer:
			'Yes. Taking part in the Charity Rounding Up is voluntary and anonymous. Before the donation is completed, the system tells you the amount you can give.',
	},
	{
		block: 'donors',
		question: 'Which project does my donation go to?',
		answer:
			'Your donation goes to the project attached to the campaign you are taking part in. You can find information about the project, the entity responsible and how the campaign is progressing through the channels Worldcoo and each company provide.',
	},
	{
		block: 'donors',
		question: 'How can I find out how much has been raised?',
		answer:
			'Worldcoo provides information on each campaign’s goals and on how it is progressing, to make it easier to follow the donations and the impact generated.',
	},
	{
		block: 'donors',
		question: 'What happens to my money after I donate?',
		answer:
			'Once the donation is made, Worldcoo manages and monitors the funds raised during the campaign. When the campaign ends, the funds go to the social entity responsible for the project to help fund it. The entity then reports on how the project developed and the impact achieved thanks to the funds raised.',
	},
	{
		block: 'donors',
		question: 'Can I get a certificate for my donation?',
		answer:
			'Yes. You can request your donation certificate at app.worldcoo.com. To do so, you will need to keep the receipt or proof of your donation, where you will find the donation code that identifies it.',
	},
	{
		block: 'donors',
		question: 'What is the donation code?',
		answer:
			'The donation code is a unique identifier attached to your contribution. You will find it on the receipt or proof of donation, and you can use it to register your donation at app.worldcoo.com and to request your donation certificate where applicable.',
	},
	{
		block: 'donors',
		question: 'How can I see all the donations I have made?',
		answer:
			'At app.worldcoo.com you can register and review the donations you have made. The platform lets you keep a history of your donations and request the corresponding certificates.',
	},
	{
		block: 'donors',
		question: 'Can I cancel a donation or ask for a refund?',
		answer:
			'Yes. You can ask for a donation to be refunded. The money donated is managed directly by Worldcoo, so Worldcoo will handle the refund of the amount donated at the customer’s request.',
		outro: 'Go to our contact section and write to us about your case.',
		link: { label: 'Contact us', target: 'contact-donor' },
	},

	// MARK: 4. Transparency and security
	{
		block: 'transparency',
		question: 'How does Worldcoo guarantee the transparency of donations?',
		answer:
			'Transparency is one of the pillars of our model. Worldcoo monitors the campaigns and provides information on the amounts raised, the projects funded and the entities taking part. We also publish information about our activity and the impact generated on a regular basis.',
	},
	{
		block: 'transparency',
		question: 'How much money reaches the social entities?',
		answer:
			'100% of the donations raised in each campaign go to the project and to the social entity responsible for carrying it out. The NGO then makes a payment to Worldcoo for the services provided.',
	},
	{
		block: 'transparency',
		question: 'How many projects has Worldcoo funded?',
		answer:
			'Since we started, Worldcoo has helped fund more than 1,000 social and environmental projects, working with hundreds of entities.',
	},
	{
		block: 'transparency',
		question: 'How many donations have been made through Worldcoo?',
		answer:
			'Millions of people have taken part in Worldcoo campaigns, and more than 100 million microdonations have been made.',
	},
	{
		block: 'transparency',
		question: 'Where can I see Worldcoo’s impact?',
		answer:
			'You can find information about Worldcoo’s campaigns, projects and the impact generated on our website. We also publish information about our activity and our impact report on a regular basis.',
	},
	{
		block: 'transparency',
		question: 'Is it safe to donate through Worldcoo?',
		answer:
			'Yes. Worldcoo’s solutions are integrated into our partner companies’ payment systems and are built following the applicable security standards. Worldcoo also monitors the campaigns and the funds raised to guarantee that donations are managed transparently.',
	},
	{
		block: 'transparency',
		question: 'Does Worldcoo store my card details?',
		answer: 'Worldcoo does not need to store your full card details for you to make a donation.',
	},
	{
		block: 'transparency',
		question: 'How is my personal data used?',
		answer:
			'Personal data is processed in accordance with our Privacy Policy and with the data protection regulations in force.',
	},

	// MARK: 5. About Worldcoo
	{
		block: 'about',
		question: 'When was Worldcoo founded?',
		answer:
			'Worldcoo was founded in 2012 with the aim of using technology to make it easier to fund social and environmental projects.',
	},
	{
		block: 'about',
		question: 'Where does Worldcoo operate?',
		answer:
			'Worldcoo works with companies and social entities in different markets and channels funds towards both national and international projects.',
	},
	{
		block: 'about',
		question: 'Is Worldcoo an NGO?',
		answer:
			'No. Worldcoo is a technology company specialising in microdonation solutions. Our aim is to connect companies, people and social entities to make collaboration easier and to generate impact.',
	},
	{
		block: 'about',
		question: 'Is Worldcoo a B Corp?',
		answer:
			'Yes. Worldcoo holds B Corp certification, which recognises a company’s commitment to high standards of social and environmental impact, transparency and accountability.',
	},
	{
		block: 'about',
		question: 'Why Worldcoo?',
		answer:
			'Because we believe donating should be easy. At Worldcoo we use technology to connect companies, people and social entities, and to turn millions of small everyday gestures into opportunities for impact. Since 2012 we have been working to make social collaboration simpler, more accessible and more transparent.',
	},
];
